import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Workflow, ArrowRight, Lock } from "lucide-react";
import client from "@/lib/api";
import { StatusBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { STAGES } from "./adminConfig";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export default function ApplicationsAdmin() {
  const [apps, setApps] = useState([]);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);

  const load = useCallback(() => {
    client.get("/records/applications", { params: { limit: 100, search } })
      .then((r) => setApps(r.data.items)).catch(() => {});
  }, [search]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const advance = async (app, stage) => {
    try {
      await client.post(`/applications/${app.id}/advance`, { stage, status: stage });
      toast.success(`Advanced to ${stage}`);
      setActive(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not advance stage");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0A192F] flex items-center gap-2"><Workflow className="h-5 w-5 text-[#0088FF]" /> Applications & Workflow</h1>
          <p className="text-sm text-slate-400">Advance candidates through the recruitment pipeline. Stages lock until required documents are verified.</p>
        </div>
        <input data-testid="app-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search candidate / job..." className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm w-56" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr><th className="text-left px-4 py-3">Candidate</th><th className="text-left px-4 py-3">Job</th><th className="text-left px-4 py-3">Country</th><th className="text-left px-4 py-3">Stage</th><th className="px-4 py-3 text-right">Action</th></tr>
            </thead>
            <tbody>
              {apps.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No applications.</td></tr>}
              {apps.map((a) => (
                <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-[#0A192F]">{a.candidate_name}<div className="text-xs text-slate-400">{a.code}</div></td>
                  <td className="px-4 py-3">{a.job_title}</td>
                  <td className="px-4 py-3">{a.country}</td>
                  <td className="px-4 py-3"><StatusBadge value={a.stage} kind="stage" /></td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" data-testid={`advance-${a.id}`} onClick={() => setActive(a)} className="bg-[#0088FF] hover:bg-[#0070E0]"><ArrowRight className="h-4 w-4 mr-1" /> Advance</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {active && (
        <Dialog open onOpenChange={() => setActive(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Advance — {active.candidate_name}</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-500 mb-3">Current stage: <StatusBadge value={active.stage} kind="stage" /></p>
            <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto">
              {STAGES.map((s) => (
                <button key={s} data-testid={`stage-${s.replace(/[^a-z]/gi, "")}`} onClick={() => advance(active, s)}
                  className="text-left px-3 py-2.5 rounded-xl border border-slate-200 hover:border-[#0088FF] hover:bg-[#0088FF]/5 text-sm flex items-center justify-between">
                  {s} {["Document Verification", "Signed Offer", "Medical", "MOL / Contract"].includes(s) && <Lock className="h-3.5 w-3.5 text-amber-500" />}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-2">Stages marked with a lock require verified documents before they can be completed.</p>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
